'use client';

import { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import Map, { Marker, Popup, type MapRef } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import { MapPin, GraduationCap, Guitar, ShoppingBag, Disc3, Star, ArrowRight, CheckCircle2, X } from 'lucide-react';
import { MAPBOX_TOKEN, MAPBOX_STYLE, useMapboxCss } from '@/lib/mapbox';
import type { LandingColors } from '@/constants/landing-content/types';

export interface LocationValue {
  latitude: number;
  longitude: number;
}

export interface DirectoryPin {
  slug: string;
  name: string;
  type?: string;
  latitude: number;
  longitude: number;
  featured?: boolean;
  is_claimed?: boolean;
  city?: string;
}

const TYPE_LABELS: Record<string, string> = {
  school: 'Escuela de música',
  rehearsal: 'Local de ensayo',
  store: 'Tienda de música',
  club: 'Club de música',
};

// Same icon-by-type mapping used in the directory map.
const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  school: GraduationCap,
  rehearsal: Guitar,
  store: ShoppingBag,
  club: Disc3,
};

export interface LocationPickerHandle {
  /** Geocode an address string and move the marker there. */
  locate: (query: string) => Promise<void>;
}

interface LocationPickerProps {
  colors: LandingColors;
  onChange: (loc: LocationValue) => void;
  /** Fill the parent height (for a side column) instead of a fixed 200px card. */
  fill?: boolean;
  /** Directory businesses to show as pins (featured ones rendered in gold). */
  pins?: DirectoryPin[];
  /** Show the draggable "your location" marker (add-business mode). */
  showMarker?: boolean;
  /** Click on a directory pin → navigate to its listing. */
  onPinClick?: (pin: DirectoryPin) => void;
}

const GOLD = '#D4A32C';

const MADRID: LocationValue = { latitude: 40.4168, longitude: -3.7038 };

async function geocode(query: string): Promise<LocationValue | null> {
  if (!query.trim() || !MAPBOX_TOKEN) return null;
  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query + ', España')}.json` +
    `?access_token=${MAPBOX_TOKEN}&country=ES&types=address,poi,place&limit=1&language=es`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.features?.length) return null;
    const [longitude, latitude] = data.features[0].center;
    return { latitude, longitude };
  } catch {
    return null;
  }
}

/**
 * Small Mapbox map with a draggable marker for the "add my business" flow.
 * Call `locate(address)` (via ref) to geocode and drop the marker; the owner can
 * drag it to fine-tune. Emits lat/lng through onChange.
 */
const LocationPicker = forwardRef<LocationPickerHandle, LocationPickerProps>(function LocationPicker(
  { colors, onChange, fill = false, pins = [], showMarker = false, onPinClick },
  ref
) {
  const mapRef = useRef<MapRef>(null);
  const cssReady = useMapboxCss();
  const [pos, setPos] = useState<LocationValue>(MADRID);
  const [activePin, setActivePin] = useState<DirectoryPin | null>(null);

  // Keep the map sized correctly once it becomes visible inside the modal.
  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.resize(), 150);
    return () => clearTimeout(t);
  }, [cssReady]);

  useImperativeHandle(ref, () => ({
    async locate(query: string) {
      const r = await geocode(query);
      if (r) {
        setPos(r);
        onChange(r);
        mapRef.current?.flyTo({ center: [r.longitude, r.latitude], zoom: 14, duration: 800 });
      }
    },
  }));

  if (!MAPBOX_TOKEN) return null;

  // Directory business pins in the Jamsom style (same as the directory map):
  // a pill with a coloured type-icon and a little pointer. Featured listings use
  // gold; the rest use the Jamsom purple.
  const directoryPins = pins.map((p) => {
    const Icon = TYPE_ICONS[p.type || ''] || MapPin;
    const dot = p.featured ? GOLD : colors.primary;
    return (
      <Marker key={p.slug} latitude={p.latitude} longitude={p.longitude} anchor="bottom" style={{ zIndex: p.featured ? 10 : 1 }}>
        <button
          type="button"
          title={p.name}
          onClick={(e) => { e.stopPropagation(); if (onPinClick) setActivePin(p); }}
          className="relative block transition-transform duration-150 hover:scale-110"
          style={{ cursor: onPinClick ? 'pointer' : 'default' }}
        >
          {p.featured && (
            <span className="absolute -right-1 -top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 shadow-sm">
              <Star className="h-2.5 w-2.5 fill-white text-white" />
            </span>
          )}
          <span
            className={`flex items-center rounded-full border-2 bg-white shadow-lg ${p.featured ? 'gap-1 border-amber-400 px-1.5 py-1' : 'border-white p-1'}`}
            style={p.featured ? { boxShadow: '0 4px 12px -2px rgba(212,163,44,0.4)' } : undefined}
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: dot }}>
              <Icon className="h-3 w-3 text-white" />
            </span>
            {p.featured && (
              <span className="max-w-[90px] truncate pr-0.5 text-[10px] font-bold text-gray-900">{p.name}</span>
            )}
          </span>
          <span
            className={`mx-auto -mt-1 block h-2 w-2 rotate-45 border-b-2 border-r-2 bg-white ${p.featured ? 'border-amber-400' : 'border-white'}`}
          />
        </button>
      </Marker>
    );
  });

  const dragMarker = showMarker ? (
    <Marker
      latitude={pos.latitude}
      longitude={pos.longitude}
      draggable
      onDragEnd={(e) => {
        const next = { latitude: e.lngLat.lat, longitude: e.lngLat.lng };
        setPos(next);
        onChange(next);
      }}
      anchor="bottom"
    >
      <MapPin className="h-9 w-9 drop-shadow-lg" style={{ color: colors.primary }} fill={colors.primary} fillOpacity={0.3} />
    </Marker>
  ) : null;

  const note = showMarker ? (
    <p className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 bg-white/85 px-3 py-1.5 text-center text-[11px] text-gray-500 backdrop-blur-sm">
      Arrastra el marcador para ajustar la ubicación
    </p>
  ) : null;

  const popup = activePin ? (
    <Popup
      latitude={activePin.latitude}
      longitude={activePin.longitude}
      anchor="bottom"
      offset={34}
      closeButton={false}
      closeOnClick={false}
      onClose={() => setActivePin(null)}
      maxWidth="260px"
      className="jamsom-pin-popup"
    >
      <div className="relative w-[240px] rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-black/5">
        <button
          type="button"
          onClick={() => setActivePin(null)}
          aria-label="Cerrar"
          className="absolute right-2.5 top-2.5 flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-3 flex items-start gap-2.5 pr-5">
          <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: activePin.featured ? `${GOLD}22` : `${colors.primary}12` }}
          >
            {(() => {
              const Icon = TYPE_ICONS[activePin.type || ''] || MapPin;
              return <Icon className="h-4 w-4" style={{ color: activePin.featured ? GOLD : colors.primary }} />;
            })()}
          </span>
          <div className="min-w-0">
            <p className="font-landing text-[15px] font-bold leading-tight text-gray-900">{activePin.name}</p>
            <p className="mt-0.5 text-xs text-gray-500">
              {TYPE_LABELS[activePin.type || ''] || 'Negocio de música'}
              {activePin.city ? ` · ${activePin.city}` : ''}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => { onPinClick?.(activePin); setActivePin(null); }}
          className="flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-bold text-white shadow-sm transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: colors.primary, boxShadow: `0 8px 16px -6px ${colors.primary}66` }}
        >
          {activePin.is_claimed ? (
            <><CheckCircle2 className="h-4 w-4" /> Ver ficha</>
          ) : (
            <>Reclamar esta ficha <ArrowRight className="h-4 w-4" /></>
          )}
        </button>
      </div>
    </Popup>
  ) : null;

  const mapChildren = (
    <>
      {directoryPins}
      {dragMarker}
      {popup}
    </>
  );

  // Override Mapbox's default popup chrome so our card shows clean, and make
  // sure the open popup sits above every pin (no more overlap).
  const popupStyles = (
    <style>{`
      .jamsom-pin-popup { z-index: 20 !important; }
      .jamsom-pin-popup .mapboxgl-popup-content {
        padding: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
        border-radius: 16px !important;
      }
      .jamsom-pin-popup .mapboxgl-popup-tip {
        border-top-color: #ffffff !important;
        filter: drop-shadow(0 2px 2px rgba(0,0,0,0.06));
      }
    `}</style>
  );

  const mapEl = (height: number | string) => (
    <Map
      ref={mapRef}
      mapLib={mapboxgl}
      mapboxAccessToken={MAPBOX_TOKEN}
      initialViewState={{ latitude: pos.latitude, longitude: pos.longitude, zoom: 11 }}
      style={{ width: '100%', height }}
      mapStyle={MAPBOX_STYLE}
      attributionControl={false}
    >
      {mapChildren}
    </Map>
  );

  // Fill mode: map occupies the full parent column (right side of the modal).
  if (fill) {
    return (
      <div className="relative h-full min-h-[320px]">
        {popupStyles}
        {!cssReady ? (
          <div className="flex h-full items-center justify-center bg-gray-50 text-sm text-gray-400">Cargando mapa…</div>
        ) : (
          <>
            {mapEl('100%')}
            {note}
          </>
        )}
      </div>
    );
  }

  // Card mode: fixed-height map inside the form flow.
  return (
    <div className="relative overflow-hidden rounded-lg border border-gray-200">
      {popupStyles}
      {!cssReady ? (
        <div className="flex h-[200px] items-center justify-center bg-gray-50 text-sm text-gray-400">Cargando mapa…</div>
      ) : (
        <>
          {mapEl(200)}
          {note}
        </>
      )}
    </div>
  );
});

export default LocationPicker;
